// Hook do pobierania pojedynczego procesu

import { axios } from 'api';
import { useQuery, UseQueryOptions } from 'react-query';
import { ICommandResponseError as IError } from 'api/types';

export interface IProcessSchema {
  version: string;
  exportedAt: string;
  classConfig?: {
    namespace: string;
    className: string;
    extends: string;
    implements: string[];
    objectType: string;
  };
  methods: Array<{
    id: string;
    name: string;
    visibility: string;
    isStatic: boolean;
    returnType: string;
    parameters: Array<{
      id: string;
      name: string;
      type: string;
      defaultValue?: string;
    }>;
    description?: string;
    isBuiltIn?: boolean;
    workflow: {
      nodes: any[];
      connections: any[];
      zoom?: number;
      pan?: { x: number; y: number };
    };
  }>;
  fields?: any[];
}

// Interfejs dla odpowiedzi API (snake_case)
export interface IApiProcessSchema {
  version: string;
  exported_at: string;
  class_config: {
    namespace: string;
    class_name: string;
    extends: string;
    implements: string[];
    object_type: string;
  };
  methods: Array<{
    id: string;
    name: string;
    visibility: string;
    is_static: boolean;
    return_type: string;
    parameters: Array<{
      id: string;
      name: string;
      type: string;
      default_value?: string;
    }>;
    description?: string;
    is_built_in?: boolean;
    workflow: {
      nodes: any[];
      connections: any[];
      zoom?: number;
      pan?: { x: number; y: number };
    };
  }>;
  fields?: any[];
}

// Interfejs dla workflow z API (snake_case)
export interface IApiWorkflow {
  name: string;
  visibility: string;
  is_static: boolean;
  return_type: string;
  parameters: Array<{
    id: string;
    name: string;
    type: string;
    default_value?: string;
  }>;
  description?: string;
  workflow: {
    nodes: any[];
    connections: Array<{
      id: string;
      source: string;
      source_output: string;
      target: string;
      target_input: string;
    }>;
    zoom?: number;
    pan?: { x: number; y: number };
  };
}

export interface IApiProcessDetails {
  id: number;
  name: string;
  description: string | null;
  type: string;
  status: string;
  module_id?: number;
  policy_kind_tag?: string | null;
  translations?: Record<string, string> | null;
  schema?: IApiProcessSchema | null;
  workflows?: IApiWorkflow[]; // Nowa struktura - workflows bezpośrednio w odpowiedzi
  last_execution?: string | null;
  execution_count?: number;
  success_rate?: number | null;
}

export interface IProcessDetails {
  id: number;
  name: string;
  description: string | null;
  type: string;
  status: string;
  moduleId?: number;
  policyKindTag?: string | null;
  translations?: Record<string, string> | null;
  schema: IProcessSchema | null;
  lastExecution: string | null;
  executionCount: number;
  successRate: number | null;
}

// Konwersja snake_case → camelCase dla kluczy obiektu
const snakeToCamelCase = (str: string): string =>
  str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

const transformNodeData = (data: any): Record<string, any> => {
  if (!data || Array.isArray(data)) return data || {};
  return Object.fromEntries(
    Object.entries(data).map(([k, v]) => [snakeToCamelCase(k), v])
  );
};

const transformNodes = (nodes: any[]): any[] =>
  (nodes || []).map((node: any) => ({
    ...node,
    data: transformNodeData(node.data)
  }));

// Funkcja transformująca workflows (nowa struktura) do methods
export const transformWorkflowsToMethods = (workflows: IApiWorkflow[]): IProcessSchema['methods'] => {
  return workflows.map((workflow, index) => ({
    id: `method-${workflow.name}-${index}`,
    name: workflow.name,
    visibility: workflow.visibility,
    isStatic: workflow.is_static,
    returnType: workflow.return_type,
    parameters: workflow.parameters.map(param => ({
      id: param.id,
      name: param.name,
      type: param.type,
      defaultValue: param.default_value
    })),
    description: workflow.description,
    isBuiltIn: false,
    workflow: {
      nodes: transformNodes(workflow.workflow.nodes),
      connections: (workflow.workflow.connections || []).map((conn: any) => ({
        id: conn.id,
        source: conn.source,
        sourceOutput: conn.source_output || conn.sourceOutput,
        target: conn.target,
        targetInput: conn.target_input || conn.targetInput
      })),
      zoom: workflow.workflow.zoom ?? 1,
      pan: workflow.workflow.pan ?? { x: 0, y: 0 }
    }
  }));
};

// Funkcja transformująca snake_case -> camelCase dla schema (stara struktura)
export const transformSchema = (apiSchema: IApiProcessSchema): IProcessSchema => {
  return {
    version: apiSchema.version || '1.0',
    exportedAt: apiSchema.exported_at || new Date().toISOString(),
    classConfig: {
      namespace: apiSchema.class_config?.namespace || '',
      className: apiSchema.class_config?.class_name || '',
      extends: apiSchema.class_config?.extends || '',
      implements: apiSchema.class_config?.implements || [],
      objectType: apiSchema.class_config?.object_type || ''
    },
    methods: apiSchema.methods.map(method => ({
      id: method.id,
      name: method.name,
      visibility: method.visibility,
      isStatic: method.is_static,
      returnType: method.return_type,
      parameters: method.parameters.map(param => ({
        id: param.id,
        name: param.name,
        type: param.type,
        defaultValue: param.default_value
      })),
      description: method.description,
      isBuiltIn: method.is_built_in,
      workflow: {
        nodes: transformNodes(method.workflow.nodes),
        connections: (method.workflow.connections || []).map((conn: any) => ({
          id: conn.id,
          source: conn.source,
          sourceOutput: conn.source_output || conn.sourceOutput,
          target: conn.target,
          targetInput: conn.target_input || conn.targetInput
        })),
        zoom: method.workflow.zoom,
        pan: method.workflow.pan
      }
    })),
    fields: apiSchema.fields
  };
};

const getProcess = async (id: number): Promise<IProcessDetails> => {
  const apiResponse: IApiProcessDetails = await axios.get(`/dev-studio/process/${id}`) as unknown as IApiProcessDetails;
  
  // Obsługa nowej struktury z workflows
  let schema: IProcessSchema | null = null;
  
  if (apiResponse.workflows && apiResponse.workflows.length > 0) {
    // Nowa struktura - workflows bezpośrednio w odpowiedzi
    const methods = transformWorkflowsToMethods(apiResponse.workflows);
    schema = {
      version: '1.0',
      exportedAt: new Date().toISOString(),
      classConfig: {
        namespace: '',
        className: '',
        extends: '',
        implements: [],
        objectType: ''
      },
      methods,
      fields: []
    };
  } else if (apiResponse.schema) {
    // Stara struktura - schema z classConfig
    schema = transformSchema(apiResponse.schema);
  }
  
  return {
    id: apiResponse.id,
    name: apiResponse.name,
    description: apiResponse.description,
    type: apiResponse.type,
    status: apiResponse.status,
    moduleId: apiResponse.module_id,
    policyKindTag: apiResponse.policy_kind_tag ?? null,
    translations: apiResponse.translations ?? null,
    schema,
    lastExecution: apiResponse.last_execution ?? null,
    executionCount: apiResponse.execution_count ?? 0,
    successRate: apiResponse.success_rate ?? null
  };
};

export const useGetProcess = (
  id: number | null,
  options?: UseQueryOptions<IProcessDetails, IError>
) => useQuery<IProcessDetails, IError>(
  ['process', id],
  () => getProcess(id!),
  {
    ...options,
    enabled: id !== null && (options?.enabled ?? true)
  }
);
